import { Injectable } from '@odinjs/odin';
import { ModalService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { isEmptyOrBlankString } from '@philips-internal/oswald-ui-utils';
import { FragmentNames } from '../../../codegen';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { SCHEDULE_OTHER_STATUS } from '../../../constants/schedule-status';
import AppointmentService from '../../../services/appointment-service';
import { showToastNotificationCaution } from '../../../utils/toast-notification-utils';
import UserContext from '../../../utils/user-context';

interface ResponseMessage {
  message: string;
}

@Injectable
export default class PseudonymCancelService {

  cancelItem(patientId: number, scheduleId: number, scheduleType: string, status: string, submitAction: () => void): void {
    if (!patientId) {
      patientId = UserContext.patientId;
    }

    this.getScheduleCancel(scheduleType, scheduleId, patientId).then(message => {
      isEmptyOrBlankString(message)
        ? this.openCancelModal(patientId, scheduleId, scheduleType, status, submitAction)
        : showToastNotificationCaution(message);
    })
  }

  confirmItem(patientId: number, scheduleCode: number, scheduleId: number, scheduleType: string, submitAction: () => void): void {
    if (!patientId) {
      patientId = UserContext.patientId;
    }

    this.openConfirmModal(scheduleCode, scheduleId, scheduleType, submitAction);
  }

  getScheduleCancel(scheduleType: string, scheduleId: number, patientId: number): Promise<string> {
    return HttpService.get(APPLICATION_ENDPOINTS.CHECK_CAN_CANCEL_AGENDA, { ieScheduleType: scheduleType, nrSeqAgenda: scheduleId, patientId: patientId })
      .then(response => {
        const responseMessage = response.body as ResponseMessage;
        return responseMessage.message;
      });
  }

  cancelWaitingList(patientId: number, scheduleId: number, submitAction: () => void): void {
    ModalService.openDialog({
      closeable: false,
      dialogDescription: 'patient.dialog.cancel-message',
      dockButtons: [
        {
          action: () => undefined,
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: () => {
            this.cancelWaitingListItem(patientId, scheduleId)
              .then(() => submitAction());
          },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ],
      title: 'patient.label.modal-scheduling-waiting-list'
    });
  }

  private openCancelModal(patientId: number, scheduleSequence: number, scheduleType: string, status: string, submitAction: () => void,): void {
    const isAppointmentRequest = scheduleType === SCHEDULE_OTHER_STATUS.REQUESTED;
    const modalTitle = isAppointmentRequest ? 'schedule.cancel-appointment-request' : 'schedule.cancel-appointment';
    const isUseNewSchedule = AppointmentService.getIsUseNewScheduleFromContext();

    const modalProps = {
      closeable: true,
      contextData: isUseNewSchedule ? {
        patientId,
        scheduleSequence,
        scheduleType,
        status,
      } : {
        ieScheduleType: scheduleType,
        ieStatus: status,
        nrSeqAgenda: scheduleSequence,
        patientId
      },
      fragment: isUseNewSchedule ? FragmentNames.PSEUDONYM_APPOINTMENTS_CANCEL : FragmentNames.APPOINTMENT_CANCEL,
      id: isUseNewSchedule ? FragmentNames.PSEUDONYM_APPOINTMENTS_CANCEL : FragmentNames.APPOINTMENT_CANCEL,
      onClose: () => { return },
      onSubmit: () => { submitAction() },
      title: modalTitle,
    };

    ModalService.openDefault(modalProps);
  }

  private openConfirmModal(scheduleCode: number, scheduleSequence: number, scheduleType: string, submitAction: () => void,): void {
    const modalProps = {
      closeable: true,
      dialogDescription: 'patient.message.appointment-confirm',
      dockButtons: [
        {
          action: () => { return },
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: (): void => {
            this.confirmAppointmentItem(scheduleCode, scheduleSequence, scheduleType).then(() => submitAction());
          },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ],
      onClose: () => { return },
      onSubmit: () => { submitAction() }
    };

    ModalService.openDialog(modalProps);
  }

  private cancelWaitingListItem(patientId: number, scheduleSequence: number): Promise<HttpResponse<void>> {
    return HttpService.delete<void>(APPLICATION_ENDPOINTS.CANCEL_WAITING_LIST_ITEM,
      { nrSequenceWaitingList: scheduleSequence, patientId: patientId });
  }

  private confirmAppointmentItem(scheduleCode: number, scheduleSequence: number, scheduleType: string): Promise<HttpResponse<void>> {
    const body = {
      scheduleCode: scheduleCode,
      scheduleDay: '',
      scheduleReasonCode: '',
      scheduleReasonDescription: '',
      scheduleSequenceNumber: scheduleSequence,
      scheduleType: scheduleType,
      statusSchedule: 'AC',
    };
    return HttpService.post(APPLICATION_ENDPOINTS.UPDATE_STATUS_APPOINTMENT, {}, { body });
  }

}
